import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';
import CoverFill from '../components/CoverFill';
import Img from '../components/Img';
import PhotoSet from '../components/PhotoSet';
import PortraitCarousel from '../components/PortraitCarousel';
import Reveal from '../components/Reveal';
import { useUi } from '../hooks/useUi';
import { shuffleArray } from '../utils/shuffle';
import {
  KIND_LABEL,
  coverFirst,
  galleryBySlug,
  imagesFor,
  isLandscape,
  isPortrait,
  nextGallery,
} from '../utils/galleries';
import { breadcrumbSchema, imageGallerySchema } from '../utils/schema';

/**
 * Gallery / trip page. Desktop: sticky info rail beside a full-height
 * hero, the writing beside a vertical-frame carousel, then every photo.
 * Mobile: full-screen hero with the title over it, then the same sections
 * stacked.
 */
const GalleryPage = ({ slug }) => {
  const gallery = galleryBySlug(slug);
  const { content, title, kind } = gallery;
  const { openMenu } = useUi();
  const [, setParams] = useSearchParams();

  const images = useMemo(() => coverFirst(shuffleArray(imagesFor(slug)), gallery.cover), [slug, gallery.cover]);
  // Skip the first few portraits — the hero above uses them.
  const portraits = useMemo(() => {
    const all = images.filter(isPortrait);
    return all.length >= 18 ? all.slice(4, 18) : all.slice(0, 14);
  }, [images]);
  const thumbs = useMemo(() => images.slice(-3), [images]);
  const vertical = images.filter(isPortrait).length;
  const horizontal = images.filter(isLandscape).length;
  const next = nextGallery(slug);
  const kindLabel = KIND_LABEL[kind];
  const indexPath = kind === 'trip' ? '/travel' : '/work';

  const altFor = (image, i) =>
    image.caption || `${title} photography by Jonny Coe${i === undefined ? '' : ` — image ${i + 1}`}`;
  const openPhoto = (image) => setParams({ photo: image.stem }, { state: { viewer: true } });

  const jsonLd = [
    breadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: kind === 'trip' ? 'Travel' : 'Work', path: indexPath },
      { name: title, path: content.path },
    ]),
    imageGallerySchema({
      name: content.seoTitle || title,
      description: content.seoDescription || content.intro || '',
      path: content.path,
      images: images.map((img) => ({ src: img.src, caption: img.caption })),
    }),
  ];

  return (
    <main className="detail">
      <SEO content={content} ogImage={`/og-${slug}.jpg`} jsonLd={jsonLd} />

      <aside className="rail">
        <div className="rail__top">
          <Link to="/" className="rail__wordmark">
            Jonny Coe
          </Link>
          <div className="rail__utility">
            <nav className="breadcrumb" aria-label="Breadcrumb">
              <Link to={indexPath}>{kind === 'trip' ? 'Travel' : 'Work'}</Link>
              <span className="breadcrumb__slash" aria-hidden="true">
                /
              </span>
              <span>{kindLabel}</span>
            </nav>
            <button type="button" className="rail__menu" onClick={openMenu}>
              Menu
            </button>
          </div>
        </div>

        <div className="rail__titleBlock">
          <h1 className="rail__title">{title}</h1>
          <span className="rail__sub">
            {kindLabel} <span className="rail__rule" aria-hidden="true" /> {images.length} photographs
          </span>
        </div>

        <dl className="rail__meta">
          <div>
            <dt>Frames</dt>
            <dd>{images.length}</dd>
          </div>
          <div>
            <dt>Type</dt>
            <dd>{kindLabel}</dd>
          </div>
          <div>
            <dt>Vertical</dt>
            <dd>{vertical}</dd>
          </div>
          <div>
            <dt>Horizontal</dt>
            <dd>{horizontal}</dd>
          </div>
        </dl>

        <a
          href="#photographs"
          className="rail__jump"
          onClick={(e) => {
            e.preventDefault();
            document.getElementById('photographs')?.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          View all {images.length} <span aria-hidden="true">↓</span>
        </a>
      </aside>

      <div className="detail__content">
        <section className="detail__hero" aria-label={`${title} cover`}>
          <CoverFill pool={images} eager altFor={altFor} />
        </section>

        <section className="overview">
          <div className="overview__text">
            <Reveal className="label">Field notes</Reveal>
            {content.intro && (
              <Reveal as="p" className="overview__lede" delay={0.05}>
                {content.intro}
              </Reveal>
            )}
            {content.essay && (
              <Reveal className="overview__body" delay={0.1}>
                {content.essay.split(/\n\s*\n/).map((para) => (
                  <p key={para.slice(0, 24)}>{para}</p>
                ))}
              </Reveal>
            )}
            <Reveal className="overview__thumbs" delay={0.15}>
              {thumbs.map((image) => (
                <button
                  type="button"
                  key={image.src}
                  className="overview__thumb"
                  onClick={() => openPhoto(image)}
                  aria-label="Open photo"
                >
                  <Img image={image} alt={altFor(image)} sizes="(max-width: 767px) 33vw, 180px" />
                </button>
              ))}
            </Reveal>
          </div>
          {portraits.length >= 3 && (
            <div className="overview__image">
              <PortraitCarousel images={portraits} altFor={altFor} onOpen={openPhoto} />
            </div>
          )}
        </section>

        <div className="detail__photos">
          <PhotoSet images={images} title={title} altFor={altFor} id="photographs" />
        </div>

        <nav className="detailNav" aria-label="More work">
          <Link to={indexPath} className="detailNav__all">
            ← All {kind === 'trip' ? 'trips' : 'work'}
          </Link>
          <Link to={next.path} className="detailNav__next">
            <span className="label">Next {KIND_LABEL[next.kind].toLowerCase()}</span>
            <span className="detailNav__title">{next.title}</span>
          </Link>
        </nav>
      </div>
    </main>
  );
};

export default GalleryPage;
